import { useState, useEffect, useCallback, useRef } from 'react';

interface Note {
  id: string;
  title: string;
  content: string;
  color: string;
  createdAt: number;
}

const COLORS = [
  { name: 'Teal', value: 'teal', bg: 'bg-teal-100 dark:bg-teal-900/30', border: 'border-teal-500', text: 'text-teal-700 dark:text-teal-300' },
  { name: 'Blue', value: 'blue', bg: 'bg-blue-100 dark:bg-blue-900/30', border: 'border-blue-500', text: 'text-blue-700 dark:text-blue-300' },
  { name: 'Purple', value: 'purple', bg: 'bg-purple-100 dark:bg-purple-900/30', border: 'border-purple-500', text: 'text-purple-700 dark:text-purple-300' },
  { name: 'Amber', value: 'amber', bg: 'bg-amber-100 dark:bg-amber-900/30', border: 'border-amber-500', text: 'text-amber-700 dark:text-amber-300' },
  { name: 'Rose', value: 'rose', bg: 'bg-rose-100 dark:bg-rose-900/30', border: 'border-rose-500', text: 'text-rose-700 dark:text-rose-300' },
  { name: 'Gray', value: 'gray', bg: 'bg-gray-100 dark:bg-gray-700/30', border: 'border-gray-500', text: 'text-gray-700 dark:text-gray-300' },
];

const STORAGE_KEY = 'masterArchitectNotes';

export default function NotesPanel() {
  const [notes, setNotes] = useState<Note[]>([]);
  const [activeNoteId, setActiveNoteId] = useState<string | null>(null);
  const [editingTitleId, setEditingTitleId] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  // Use a ref instead of state to avoid re-render loops in useCallback
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Load notes from localStorage
  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setNotes(parsed);
        if (parsed.length > 0) {
          setActiveNoteId(parsed[0].id);
        }
      } catch (e) {
        console.error('Failed to parse notes:', e);
      }
    }
  }, []);

  // Auto-save to localStorage (debounced) — stable reference, no stale closure
  const saveNotes = useCallback((notesToSave: Note[]) => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(() => {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notesToSave));
    }, 500);
  }, []); // no deps needed — uses ref, never stale

  const createNewNote = () => {
    const newNote: Note = {
      id: Date.now().toString(),
      title: `Note ${notes.length + 1}`,
      content: '',
      color: 'teal',
      createdAt: Date.now(),
    };
    const updatedNotes = [newNote, ...notes];
    setNotes(updatedNotes);
    setActiveNoteId(newNote.id);
    saveNotes(updatedNotes);
  };

  const updateNoteTitle = (id: string, title: string) => {
    const updatedNotes = notes.map(note =>
      note.id === id ? { ...note, title } : note
    );
    setNotes(updatedNotes);
    saveNotes(updatedNotes);
  };

  const updateNoteContent = (id: string, content: string) => {
    const updatedNotes = notes.map(note =>
      note.id === id ? { ...note, content } : note
    );
    setNotes(updatedNotes);
    saveNotes(updatedNotes);
  };

  const updateNoteColor = (id: string, color: string) => {
    const updatedNotes = notes.map(note =>
      note.id === id ? { ...note, color } : note
    );
    setNotes(updatedNotes);
    saveNotes(updatedNotes);
  };

  const deleteNote = (id: string) => {
    if (deleteConfirmId === id) {
      const updatedNotes = notes.filter(note => note.id !== id);
      setNotes(updatedNotes);
      saveNotes(updatedNotes);
      setDeleteConfirmId(null);
      if (activeNoteId === id) {
        setActiveNoteId(updatedNotes.length > 0 ? updatedNotes[0].id : null);
      }
    } else {
      setDeleteConfirmId(id);
      setTimeout(() => setDeleteConfirmId(null), 3000);
    }
  };

  const activeNote = notes.find(note => note.id === activeNoteId);
  const activeColor = COLORS.find(c => c.value === activeNote?.color) || COLORS[0];

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center">
              <i className="ri-sticky-note-line text-xl text-teal-600 dark:text-teal-400"></i>
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white">Design Notes</h3>
            {notes.length > 0 && (
              <span className="px-2 py-0.5 text-xs font-medium bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-300 rounded-full whitespace-nowrap">
                {notes.length}
              </span>
            )}
          </div>
          <button
            onClick={createNewNote}
            className="px-3 py-1.5 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors whitespace-nowrap flex items-center gap-1.5"
          >
            <i className="ri-add-line text-base"></i>
            New Note
          </button>
        </div>
      </div>

      {notes.length === 0 ? (
        // Empty State
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center max-w-xs">
            <div className="w-20 h-20 mx-auto mb-4 flex items-center justify-center bg-gray-100 dark:bg-gray-800 rounded-full">
              <i className="ri-sticky-note-line text-4xl text-gray-400 dark:text-gray-500"></i>
            </div>
            <h4 className="text-base font-semibold text-gray-900 dark:text-white mb-2">No Notes Yet</h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Start capturing your design ideas, requirements, and inspiration.
            </p>
            <button
              onClick={createNewNote}
              className="px-4 py-2 text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 rounded-lg transition-colors whitespace-nowrap"
            >
              Create Your First Note
            </button>
          </div>
        </div>
      ) : (
        // Notes Layout
        <div className="flex-1 flex overflow-hidden">
          {/* Notes List Sidebar */}
          <div className="w-48 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
            {notes.map(note => {
              const noteColor = COLORS.find(c => c.value === note.color) || COLORS[0];
              return (
                <button
                  key={note.id}
                  onClick={() => setActiveNoteId(note.id)}
                  className={`w-full p-3 text-left border-b border-gray-200 dark:border-gray-700 transition-colors ${
                    activeNoteId === note.id
                      ? 'bg-teal-50 dark:bg-teal-900/20'
                      : 'hover:bg-gray-50 dark:hover:bg-gray-800'
                  }`}
                >
                  <div className="flex items-start gap-2 mb-1">
                    <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${noteColor.bg} ${noteColor.border} border`}></div>
                    <h4 className="text-sm font-medium text-gray-900 dark:text-white line-clamp-2 flex-1">
                      {note.title}
                    </h4>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 ml-4">
                    {note.content || 'Empty note'}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1 ml-4">
                    {new Date(note.createdAt).toLocaleDateString()}
                  </p>
                </button>
              );
            })}
          </div>

          {/* Active Note Editor */}
          {activeNote && (
            <div className="flex-1 flex flex-col overflow-hidden">
              {/* Note Header */}
              <div className={`p-4 border-b border-gray-200 dark:border-gray-700 ${activeColor.bg}`}>
                {editingTitleId === activeNote.id ? (
                  <input
                    type="text"
                    value={activeNote.title}
                    onChange={(e) => updateNoteTitle(activeNote.id, e.target.value)}
                    onBlur={() => setEditingTitleId(null)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') setEditingTitleId(null);
                    }}
                    autoFocus
                    className="w-full px-2 py-1 text-base font-semibold bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:ring-2 focus:ring-teal-500"
                  />
                ) : (
                  <div className="flex items-center justify-between">
                    <h3
                      onClick={() => setEditingTitleId(activeNote.id)}
                      className={`text-base font-semibold cursor-pointer hover:opacity-80 ${activeColor.text}`}
                    >
                      {activeNote.title}
                    </h3>
                    <button
                      onClick={() => deleteNote(activeNote.id)}
                      className={`px-3 py-1 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                        deleteConfirmId === activeNote.id
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                      }`}
                    >
                      {deleteConfirmId === activeNote.id ? 'Confirm Delete?' : 'Delete'}
                    </button>
                  </div>
                )}
                <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Created {new Date(activeNote.createdAt).toLocaleString()}
                </p>
              </div>

              {/* Color Picker */}
              <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-medium text-gray-700 dark:text-gray-300">Label:</span>
                  <div className="flex gap-2">
                    {COLORS.map(color => (
                      <button
                        key={color.value}
                        onClick={() => updateNoteColor(activeNote.id, color.value)}
                        className={`w-6 h-6 rounded-full border-2 transition-all ${color.bg} ${
                          activeNote.color === color.value
                            ? `${color.border} scale-110`
                            : 'border-transparent hover:scale-105'
                        }`}
                        title={color.name}
                      ></button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Note Content */}
              <div className="flex-1 p-4 overflow-y-auto">
                <textarea
                  value={activeNote.content}
                  onChange={(e) => updateNoteContent(activeNote.id, e.target.value)}
                  placeholder="Start typing your design ideas, requirements, or inspiration..."
                  className="w-full h-full p-3 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white border border-gray-300 dark:border-gray-600 rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-teal-500"
                />
              </div>

              {/* Character Count */}
              <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <p className="text-xs text-gray-500 dark:text-gray-400 text-right">
                  {activeNote.content.length} characters
                </p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}